import { useState } from "react";
import {
  useLLMModels,
  usePatchLLMConfig,
  type LLMConfig,
  type LLMModel,
} from "@/api/admin/llm-config";
import { ApiError } from "@/api/client";
import { AdminErrorState } from "../AdminErrorState";
import { useConfirmedChange } from "../confirmChange";
import type { ChangeField } from "../ConfirmModal";
import type { Risk } from "../risk";
import { useAdminT } from "../useAdminT";
import { BTN, COL, FIELD, LABEL, ROW, VALUE } from "./styles";

interface Props {
  /** llm_config operation name. */
  settingKey: string;
  name: string;
  risk: Risk;
  row: LLMConfig;
}

function estimateUSD(caps: LLMModel | undefined, maxTokens: number): number {
  if (!caps) return 0;
  // Catalogue prices are per 1M tokens; this works in thousands.
  // Assume ~500 input tokens average across prompts; max_tokens caps output.
  return (caps.price_in_per_1m / 1000) * 0.5 + (caps.price_out_per_1m / 1000) * (maxTokens / 1000);
}

/** The 422s from `PATCH /api/admin/llm-config/{op}` carry a `detail.message`
 * naming the rejected (model, reasoning_effort) combination. */
function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: { message?: unknown } } | null)?.detail;
    if (typeof detail?.message === "string") return detail.message;
  }
  return e instanceof Error ? e.message : String(e);
}

const fmt = (v: unknown): string => (v === null || v === undefined || v === "" ? "—" : String(v));

/** llm_config row: model, max_tokens, temperature, effort, verbosity, enabled. */
export function LlmEditor({ settingKey, name, risk, row }: Props): JSX.Element {
  const t = useAdminT();
  const models = useLLMModels();
  const patch = usePatchLLMConfig();
  const { confirmChange, modal } = useConfirmedChange();
  const [fragment, setFragment] = useState<Partial<LLMConfig> | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  if (models.isError) return <AdminErrorState onRetry={() => void models.refetch()} />;
  if (models.isLoading) return <div>{t("common.loading")}</div>;

  const v: LLMConfig = { ...row, ...fragment };
  const dirty = fragment !== null;
  const catalogue = models.data ?? [];
  // Mirrors `resolve_model` on the backend: a dated snapshot
  // (`gpt-5-mini-2025-08-07`) is its family; anything else is unknown.
  const capsOf = (model: string): LLMModel | undefined => {
    const family = model.replace(/-\d{4}-\d{2}-\d{2}$/, "");
    return catalogue.find((m) => m.name === family);
  };
  const caps = capsOf(v.model);
  const setDraft = (f: Partial<LLMConfig>): void => setFragment((d) => ({ ...d, ...f }));

  function save(): void {
    if (!fragment) return;
    // The backend PATCH treats a missing key as "leave as-is" and can't clear
    // `temperature` to null, so an emptied field is omitted from the body.
    // `reasoning_effort` / `verbosity` take an explicit `null` to clear.
    const { temperature, ...rest } = fragment;
    const patchBody: Parameters<typeof patch.mutate>[0]["patch"] = { ...rest };
    if (typeof temperature === "number") patchBody.temperature = temperature;
    patch.mutate(
      { operation: settingKey, patch: patchBody },
      {
        onSuccess: () => {
          setStatus(`${settingKey} updated`);
          setFragment(null);
        },
        onError: (e) => setStatus(errorMessage(e)),
      }
    );
  }

  function requestSave(): void {
    if (!fragment) return;
    const keys = Object.keys(fragment) as (keyof LLMConfig)[];
    const before: ChangeField[] = keys.map((k) => ({ label: k, value: fmt(row[k]) }));
    const after: ChangeField[] = keys.map((k) => ({ label: k, value: fmt(v[k]) }));
    confirmChange({ name, risk }, before, after, save);
  }

  return (
    <div style={COL}>
      {status !== null && (
        <div role="status" style={LABEL}>
          {status}
        </div>
      )}
      <div style={ROW}>
        <span />
        <label>
          <span style={LABEL}>{t("llm_config.enabled")}</span>{" "}
          <input
            type="checkbox"
            checked={v.is_enabled}
            onChange={(e) => setDraft({ is_enabled: e.target.checked })}
          />
        </label>
      </div>
      {/* `notes` is the per-operation hint, seeded in migrations. */}
      {row.notes !== null && row.notes !== "" && <div style={LABEL}>{row.notes}</div>}
      <div style={LABEL}>{t("llm_config.enabled_hint")}</div>
      <label>
        <span style={LABEL}>Model </span>
        <select
          aria-label={`${settingKey} model`}
          style={FIELD}
          value={v.model}
          onChange={(e) => {
            const model = e.target.value;
            const next = capsOf(model);
            const f: Partial<LLMConfig> = { model };
            // The API validates the row as it will look *after* the patch, so
            // an effort/verbosity the new model does not take goes out cleared
            // in the same body or the PATCH 422s.
            const effort = v.reasoning_effort;
            if (!next?.reasoning || (effort !== null && !next.efforts.includes(effort))) {
              f.reasoning_effort = null;
            }
            if (!next?.verbosity) f.verbosity = null;
            setDraft(f);
          }}
        >
          {catalogue.map((m) => (
            <option key={m.name} value={m.name}>
              {m.name}
            </option>
          ))}
          {/* A model retired from the catalogue still has to render. */}
          {caps === undefined && <option value={v.model}>{v.model}</option>}
        </select>
      </label>
      <label>
        <span style={LABEL}>max_tokens </span>
        <input
          style={FIELD}
          type="number"
          value={v.max_tokens}
          onChange={(e) => setDraft({ max_tokens: Number(e.target.value) })}
        />
      </label>
      <label>
        <span style={LABEL}>temperature </span>
        <input
          style={FIELD}
          type="number"
          step="0.1"
          placeholder={v.temperature === null ? "—" : undefined}
          value={v.temperature ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            setDraft({ temperature: raw === "" ? null : Number(raw) });
          }}
        />
      </label>
      {caps?.reasoning === true && (
        <>
          <div style={LABEL}>{t("llm_config.temperature_ignored")}</div>
          <label>
            <span style={LABEL}>reasoning_effort </span>
            <select
              aria-label={`${settingKey} reasoning_effort`}
              style={FIELD}
              value={v.reasoning_effort ?? ""}
              onChange={(e) =>
                setDraft({ reasoning_effort: e.target.value === "" ? null : e.target.value })
              }
            >
              <option value="">
                {t("llm_config.effort_default", { effort: caps.default_effort })}
              </option>
              {caps.efforts.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      {caps?.verbosity === true && (
        <label>
          <span style={LABEL}>verbosity </span>
          <select
            aria-label={`${settingKey} verbosity`}
            style={FIELD}
            value={v.verbosity ?? ""}
            onChange={(e) => setDraft({ verbosity: e.target.value === "" ? null : e.target.value })}
          >
            <option value="">{t("llm_config.verbosity_default")}</option>
            {["low", "medium", "high"].map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </label>
      )}
      <div style={VALUE}>
        {t("llm_config.estimated_cost")}: ${estimateUSD(caps, v.max_tokens).toFixed(4)}
      </div>
      {dirty && (
        <div style={{ ...ROW, justifyContent: "flex-end" }}>
          <button style={BTN} onClick={requestSave}>
            {t("common.save")}
          </button>
        </div>
      )}
      {modal}
    </div>
  );
}
